import { shopifyComponentCopy } from '@testfeed/product-language'
import { InfoButton, InfoPopover } from '../../shared/PageHelp'
import { Progress } from '../art/Art'
import { HomeArt } from '../art/HomeArt'
import { VERDICT_LABEL } from '../derive'
import type { TestSummary } from '../types'

const INFO_ID = 'app-home-info-recent-tests'
const WHY =
  "Shows your last five tests and how they finished, so you can see what's worked without digging through history."
const HOW =
  'Results come from simulated shoppers who saw each version, compared against a control group. Percentages compare against that control.'

type Mode = { kind: 'setup' } | { kind: 'paused' } | { kind: 'ready'; onNewTest: () => void }

interface Props {
  tests: TestSummary[]
  mode: Mode
  usage: { used: number; included: number } | null
  go: (to: string) => void
}

function Result({ t }: { t: TestSummary }) {
  const copy = shopifyComponentCopy.appHome
  if (t.status === 'running')
    return (
      <s-grid gap="small-400">
        <Progress value={t.progress} label={copy.recentTestsProgressLabel.replace('{name}', t.name)} />
        <s-text color="subdued">
          {copy.recentTestsPercentAnsweredLabel.replace('{percent}', String(Math.round(t.progress)))}
        </s-text>
      </s-grid>
    )
  if (t.status === 'failed') return <s-text color="subdued">{copy.recentTestsNoResultLabel}</s-text>
  const tone = t.verdict === 'clear' ? 'success' : t.verdict === 'leaning' ? 'info' : 'neutral'
  return (
    <s-stack direction="inline" gap="small-200" alignItems="center">
      <s-badge tone={tone}>{t.winner ? `${VERDICT_LABEL[t.verdict]}: ${t.winner}` : VERDICT_LABEL[t.verdict]}</s-badge>
      {t.verdict !== 'tie' && <s-text color="subdued">+{t.gap} in every 100</s-text>}
    </s-stack>
  )
}

function Status({ t }: { t: TestSummary }) {
  const copy = shopifyComponentCopy.appHome
  if (t.status === 'running')
    return (
      <s-badge tone="info" icon="clock">
        {copy.recentTestsRunningLabel}
      </s-badge>
    )
  if (t.status === 'failed') return <s-badge tone="critical">{copy.recentTestsDidntFinishLabel}</s-badge>
  return <s-text color="subdued">{t.completedAt}</s-text>
}

/** Recent tests, or an empty state that says what will appear here. */
export function RecentTests({ tests, mode, usage, go }: Props) {
  const copy = shopifyComponentCopy.appHome
  const heading = (
    <s-grid gridTemplateColumns="1fr auto auto" alignItems="center" gap="small">
      <s-heading>{copy.recentTestsHeading}</s-heading>
      {usage && (
        <s-text color="subdued">
          {copy.recentTestsUsageLabel
            .replace('{used}', String(usage.used))
            .replace('{included}', String(usage.included))}
        </s-text>
      )}
      <InfoButton id={INFO_ID} heading={copy.recentTestsHeading} />
    </s-grid>
  )

  if (tests.length === 0) {
    const setup = mode.kind === 'setup'
    return (
      <>
        <s-section accessibilityLabel={copy.recentTestsHeading}>
          <s-grid gap="base">
            {heading}
            <s-grid
              gridTemplateColumns="@container (inline-size <= 420px) 1fr, auto 1fr"
              gap="base"
              alignItems="center"
            >
              <HomeArt name="results" alt="Example test results report" size={64} background="transparent" />
              <s-grid gap="small-200">
                <s-text type="strong">
                  {setup ? copy.recentTestsEmptySetupTitle : copy.recentTestsEmptyReadyTitle}
                </s-text>
                <s-paragraph color="subdued">
                  {setup ? copy.recentTestsEmptySetupBody : copy.recentTestsEmptyReadyBody}
                </s-paragraph>
                {mode.kind === 'ready' && (
                  <s-stack direction="inline">
                    <s-button onClick={mode.onNewTest}>{copy.startTestAction}</s-button>
                  </s-stack>
                )}
              </s-grid>
            </s-grid>
          </s-grid>
        </s-section>
        <InfoPopover id={INFO_ID} why={WHY} how={HOW} />
      </>
    )
  }

  return (
    <>
      <s-section accessibilityLabel={copy.recentTestsHeading} padding="none">
        <s-box padding="base" paddingBlockEnd="small">
          {heading}
        </s-box>
        <s-table>
          <s-table-header-row>
            <s-table-header listSlot="primary">Test</s-table-header>
            <s-table-header listSlot="secondary">Status</s-table-header>
            <s-table-header listSlot="labeled">Result</s-table-header>
          </s-table-header-row>
          <s-table-body>
            {tests.slice(0, 5).map((t) => (
              <s-table-row key={t.id} clickDelegate={`test-${t.id}`}>
                <s-table-cell>
                  <s-link id={`test-${t.id}`} onClick={() => go(`/app/tests/${t.id}`)}>
                    {t.name}
                  </s-link>
                </s-table-cell>
                <s-table-cell>
                  <Status t={t} />
                </s-table-cell>
                <s-table-cell>
                  <Result t={t} />
                </s-table-cell>
              </s-table-row>
            ))}
          </s-table-body>
        </s-table>
        <s-box padding="base">
          <s-stack direction="inline" justifyContent="center">
            <s-link onClick={() => go('/app/tests')}>{copy.recentTestsSeeAllAction}</s-link>
          </s-stack>
        </s-box>
      </s-section>
      <InfoPopover id={INFO_ID} why={WHY} how={HOW} />
    </>
  )
}
